import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "tss-react/mui";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, MEDIA } from "./lightTheme";
import { getEnv } from "config/Config";
import { Context } from "states/Store";
import { formatDt, normColor, transformDocuments } from "./utils/lightUtils";
import { getDoctypeTranslatedName } from "services/common/DocumentUtils";
import LightStatusChip from "./components/LightStatusChip";
import StopConfirmDialog from "./components/StopConfirmDialog";
import DocumentRow from "./components/DocumentRow";
import useMentionAutocomplete from "./hooks/useMentionAutocomplete";
import LightNotesList from "./components/LightNotesList";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { getDossierSheet, getDossierIndicators, stopDossier, reactivateDossier, updateRequiredDocuments } from "services/edge/DossierService";
import { getAccountRequirements } from "services/edge/AccountService";
import { isSealedDocument } from "services/common/Constants";
import { getAllNoteDossier, createNoteDossier, deleteNoteDossier } from "services/edge/NoteService";
import { getDossierAttributes, getAccountAttributesByBondType } from "services/edge/AttributesService";
import { getAccountUsers } from "services/edge/UserService";
import { getApiErrorMessageCode } from "services/common/ApiUtils";
import { ERROR, SUCCESS } from "services/common/Constants";
import ROUTES, { getUrlWithAccountId } from "config/Routes";
import LightLoader from "./LightLoader";
import LightDocViewer from "./LightDocViewer";
import LightEditableAttributes from "./LightEditableAttributes";
import { formatCalculationResult } from "services/common/CalculatorUtils";
import { getContextThirdpartyByTPid, getContextInstances } from "services/edge/ContexteService";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { RoleBasedAccessControlContext, PrivilegeEnum } from "services/common/RolesUtils";


// ─── Styles (local only — shared styles come from useLightStyles) ──────────
const useStyles = makeStyles()(() => ({
  heroStatus: {
    display: "inline-flex", alignItems: "center",
    borderRadius: "20px", padding: "5px 14px",
    ...FONT.small, fontWeight: 700,
    fontFamily: FONT.family, flexShrink: 0,
  },
  heroBody: {
    backgroundColor: BRAND.white,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 0,
    [MEDIA.smDown]: { gridTemplateColumns: "1fr" },
  },
  heroPanel: {
    padding: "1.5rem 2rem",
    display: "flex", flexDirection: "column", gap: "0.75rem",
    [MEDIA.smDown]: { padding: "1.1rem" },
  },
  heroPanelLabel: {
    ...FONT.meta, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.09em", color: BRAND.textMuted,
    fontFamily: FONT.family, marginBottom: "0.25rem",
  },
  heroPanelName: {
    fontSize: "1rem", fontWeight: 700, color: BRAND.textMain,
    fontFamily: FONT.family,
  },
  heroPanelRow: {
    display: "flex", alignItems: "flex-start", gap: "0.5rem",
    ...FONT.body, fontSize: "0.825rem", color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
  heroPanelValue: {
    fontSize: "0.825rem", fontWeight: 500, color: BRAND.textMain,
    fontFamily: FONT.family,
  },

  // ── Documents ──
  docList: {
    display: "flex", flexDirection: "column", gap: "0.5rem",
    padding: "1.25rem 1.5rem",
    [MEDIA.smDown]: { padding: "1rem" },
  },
  docRow: {
    display: "flex", alignItems: "center", gap: "0.75rem",
    padding: "0.75rem 1rem", borderRadius: RADIUS.md,
    backgroundColor: BRAND.white, border: `1px solid ${BRAND.border}`,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": { borderColor: BRAND.primary, boxShadow: SHADOW.soft },
    [MEDIA.smDown]: { flexWrap: "wrap", gap: "0.5rem" },
  },
  docName: {
    ...FONT.body, fontWeight: 600, color: BRAND.textMain,
    fontFamily: FONT.family, flex: 1, minWidth: 0,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    [MEDIA.smDown]: {
      flex: "1 1 100%", whiteSpace: "normal",
      overflow: "visible", textOverflow: "clip",
      wordBreak: "break-word",
    },
  },
  docMeta: {
    ...FONT.small, color: BRAND.textMuted,
    fontFamily: FONT.family, flexShrink: 0, whiteSpace: "nowrap",
  },
  docStatusChip: {
    display: "inline-flex", alignItems: "center",
    borderRadius: "20px", padding: "3px 10px",
    ...FONT.tiny, fontFamily: FONT.family, flexShrink: 0,
  },
  docActionBtn: {
    display: "flex", alignItems: "center", gap: "0.25rem",
    ...FONT.meta, color: BRAND.primary, fontFamily: FONT.family,
    whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer",
    background: "none", border: "none", padding: 0,
    opacity: 0.7, transition: "opacity 0.15s",
    "&:hover": { opacity: 1 },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },

  // ── Notes ──
  noteInput: {
    padding: "1.25rem 1.5rem",
    borderBottom: `1px solid ${BRAND.border}`,
    display: "flex", gap: "0.75rem", alignItems: "center",
    [MEDIA.smDown]: { padding: "1rem", gap: "0.5rem" },
  },
  noteTextarea: {
    flex: 1, borderRadius: RADIUS.md,
    border: `1.5px solid ${BRAND.border}`,
    padding: "0.65rem 0.85rem", fontFamily: FONT.family,
    ...FONT.body, fontSize: "0.875rem", resize: "none", outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
    backgroundColor: BRAND.background, color: BRAND.textMain,
    lineHeight: 1.5, minHeight: "60px",
  },
  noteSendBtn: {
    height: "40px", width: "40px", borderRadius: RADIUS.md,
    border: "none", backgroundColor: BRAND.primary,
    color: BRAND.white, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },
  notesList: {
    display: "flex", flexDirection: "column",
    padding: "0.5rem 0",
  },
  noteItem: {
    padding: "1rem 1.5rem",
    display: "flex", flexDirection: "column", gap: "0.4rem",
    borderBottom: `1px solid ${BRAND.border}`,
    "&:last-child": { borderBottom: "none" },
  },
  noteHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  noteAuthor: {
    ...FONT.body, fontWeight: 700, color: BRAND.textMain,
    fontFamily: FONT.family,
  },
  noteDate: {
    ...FONT.meta, color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
  noteContent: {
    ...FONT.body, fontSize: "0.875rem", color: BRAND.textMain,
    fontFamily: FONT.family, lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  noteDeleteBtn: {
    padding: "2px 6px", borderRadius: RADIUS.sm, border: "none",
    background: "transparent", cursor: "pointer",
    color: BRAND.textMuted, display: "flex", alignItems: "center",
    transition: "color 0.15s, background 0.15s",
    "&:hover": { color: "#E84343", backgroundColor: "#FEF2F2" },
  },
  emptyNote: {
    padding: "2rem 1.5rem", textAlign: "center",
    ...FONT.body, color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
  noteInputWrap: {
    position: "relative",
  },
  mentionDropdown: {
    position: "absolute", zIndex: 200,
    top: "100%", left: 0, right: 0,
    marginTop: "4px",
    backgroundColor: BRAND.white,
    border: `1.5px solid ${BRAND.border}`,
    borderRadius: RADIUS.md,
    boxShadow: SHADOW.strong,
    overflow: "hidden",
    maxHeight: "180px", overflowY: "auto",
  },
  mentionItem: {
    display: "flex", alignItems: "center", gap: "0.6rem",
    padding: "0.5rem 0.85rem", cursor: "pointer",
    transition: "background 0.1s",
    "&:hover": { backgroundColor: BRAND.background },
  },
  mentionItemActive: {
    backgroundColor: BRAND.background,
  },
  mentionAvatar: {
    width: "26px", height: "26px", borderRadius: "50%",
    backgroundColor: BRAND.primary, color: BRAND.white,
    display: "flex", alignItems: "center", justifyContent: "center",
    ...FONT.tiny, fontWeight: 700, flexShrink: 0,
  },
  mentionName: {
    ...FONT.body, fontWeight: 500, color: BRAND.textMain,
    fontFamily: FONT.family,
  },

}));

// ─── Component ────────────────────────────────────────────────────────────────
export default function LightDossierDetail() {
  const { classes: s } = useStyles();
  const { classes: light } = useLightStyles();
  const { dossierId } = useParams();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const canArchiveDossier  = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_DOSSIER);
  const canEditDossier     = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER);
  const canViewDossierNotes = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_NOTES);
  const canCreateDossierNotes = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_NOTES);
  const canViewDossierIndicators = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR);
  const canViewDossierAttributes = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE);
  const canEditDossierAttributes = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE);
  const history = useHistory();
  const location = useLocation();
  const fromEvaluations = location.state?.fromEvaluations ?? false;
  const evalFilters = location.state?.filters ?? null;
  const evalPage = location.state?.page ?? 0;
  const evalListState = location.state ?? {}; // all state from evaluations list

  const [dossier, setDossier] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [notes, setNotes] = useState([]);
  const [notesOpen, setNotesOpen] = useState(false);
  const [dossierAttrs, setDossierAttrs] = useState([]);
  const [editDossierAttrsOpen, setEditDossierAttrsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [accountUsers, setAccountUsers] = useState([]);
  const mention = useMentionAutocomplete(accountUsers, noteText, setNoteText);
  const [hasDossierAttrDefs, setHasDossierAttrDefs] = useState(false);

  // Context for this dossier
  const [dossierContext, setDossierContext] = useState(null); // { reference, modelName, instanceId }

  // Manage documents
  const [addDocOpen, setAddDocOpen] = useState(false);
  const [accountReqs, setAccountReqs] = useState(null);
  const [selectedNewDocs, setSelectedNewDocs] = useState([]);
  const [addingDocs, setAddingDocs] = useState(false);
  const [docFilter, setDocFilter] = useState("");

  // Stop dossier
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopReason, setStopReason] = useState("");
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!state.account?.id || !dossierId) return;
    if (!silent) setLoading(true);
    try {
      const [dossierRes, notesRes] = await Promise.all([
        getDossierSheet(state.account.id, dossierId, i18next.language, dispatch),
        getAllNoteDossier(state.account.id, dossierId, null),
      ]);
      const d = dossierRes.content;
      setDossier(d);
      dispatch({ type: "DOSSIER", dossier: d });
      setNotes(notesRes?.content ?? []);

      // Indicators, users, attributes in parallel (optional, don't fail on error)
      await Promise.allSettled([
        getDossierIndicators(state.account.id, dossierId, dispatch)
          .then((r) => setIndicators(r?.content ?? [])),
        getAccountUsers(state.account.id, dispatch)
          .then((r) => setAccountUsers(r?.content ?? [])),
        getDossierAttributes(state.account.id, dossierId, null)
          .then((r) => setDossierAttrs((r?.content ?? []).filter((a) => a.values?.length > 0))),
        getAccountAttributesByBondType(state.account.id, "dossier", null)
          .then((r) => setHasDossierAttrDefs((r?.content ?? []).length > 0))
          .catch(() => {}),
        d.thirdpartyId
          ? Promise.all([
              getContextThirdpartyByTPid(state.account.id, d.thirdpartyId, dispatch),
              getContextInstances(state.account.id, true, dispatch),
            ]).then(([ctxTps, instances]) => {
              const byId = Object.fromEntries((instances?.content ?? []).map((i) => [String(i.id), i]));
              const match = (Array.isArray(ctxTps) ? ctxTps : [])
                .find((ct) => (ct.dossierIds ?? []).includes(parseInt(dossierId, 10)) || (ct.dossierIds ?? []).includes(dossierId));
              if (match) {
                const inst = byId[String(match.ctxInstanceId)];
                setDossierContext({
                  reference: inst?.reference || `#${match.ctxInstanceId}`,
                  modelName: inst?.contextModelName || "",
                  instanceId: match.ctxInstanceId,
                });
              }
            }).catch(() => {})
          : Promise.resolve(),
      ]);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [state.account?.id, dossierId, dispatch]);

  useEffect(() => { load(); }, [load]);

  // Reopen doc viewer after returning from external form
  useEffect(() => {
    const rd = location.state?.reopenDoc;
    if (!rd || !dossier) return;
    // Find the matching doc from freshly loaded data
    const docs = (dossier.documents ?? []);
    const match = docs.find((d) => {
      const ev = [].concat(d.evidences ?? [], (d.historicEvidences ?? []).filter((e) => e.statusReason !== "ARRET_DEMANDE"));
      const rId = ev[0]?.responseId;
      return (rd.reponseId && rId === rd.reponseId) || (rd.typeDocumentId && d.typeDocumentId === rd.typeDocumentId);
    });
    if (match) {
      const ev = [].concat(match.evidences ?? [], (match.historicEvidences ?? []).filter((e) => e.statusReason !== "ARRET_DEMANDE"));
      setViewerDoc({ ...match, reponseId: ev[0]?.responseId, fileId: ev[0]?.id, evidences: ev, canOpen: true });
    } else {
      setViewerDoc(rd);
    }
    history.replace(location.pathname, { ...location.state, reopenDoc: undefined });
  }, [dossier]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      // Résoudre les mentions : retrouver les userOid des @NomPrenom présents dans le texte
      const mentionRegexp = /@([\w\u00C0-\u017E]+ [\w\u00C0-\u017E]+)/g;
      const matched = [...noteText.matchAll(mentionRegexp)].map((m) => m[1]);
      const filteredUsers = mention.selectedMentions
        .filter((m) => matched.includes(m.nomPrenom))
        .map((m) => m.userOid);

      await createNoteDossier(state.account.id, dossierId, {
        content: noteText.trim(),
        selectedUsersIds: filteredUsers,
      }, dispatch);
      setNoteText("");
      mention.resetMentions();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setSavingNote(false);
    }
    // Refresh toujours, hors try/catch (comme NoteInput.js → fetchNotes())
    try {
      const res = await getAllNoteDossier(state.account.id, dossierId, null);
      setNotes(res?.content ?? []);
    } catch (_) { /* silent — note créée, refresh seul échoue */ }
  };

  const handleDeleteNote = async (noteOId) => {
    try {
      await deleteNoteDossier(state.account.id, dossierId, noteOId, dispatch);
      setNotes((prev) => prev.filter((n) => n.noteOId !== noteOId));
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    }
  };

  const isLocked = dossier?.dossierTypeLock === true;
  const isStopped = Boolean(dossier?.stoppingDate);

  const handleOpenAddDoc = async () => {
    setAddDocOpen(true);
    setDocFilter("");
    setSelectedNewDocs(documents.map((d) => d.documentCode));
    if (accountReqs === null) {
      try {
        const res = await getAccountRequirements(state.account.id, dispatch);
        setAccountReqs(res?.content ?? []);
      } catch (_) { setAccountReqs([]); }
    }
  };

  const handleConfirmDocs = async () => {
    setAddingDocs(true);
    try {
      await updateRequiredDocuments(state.account.id, dossierId, { newRequiredDocuments: selectedNewDocs }, dispatch);
      dispatch({ type: "ALERT", alert: { type: "SUCCESS", msg: i18next.t("light.detail.docsUpdated") } });
      setAddDocOpen(false);
      load();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setAddingDocs(false);
    }
  };

  const handleConsultDoc = (doc) => {
    if (!doc.canOpen) return;
    setViewerDoc(doc);
  };

  const handleStopDossier = async () => {
    setStopping(true);
    try {
      await stopDossier(state.account.id, dossierId, stopReason.trim(), dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.stopped") } });
      setStopDialogOpen(false);
      setStopReason("");
      history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${dossier.thirdpartyId}`));
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setStopping(false);
    }
  };

  const [reactivating, setReactivating] = useState(false);
  const handleReactivateDossier = async () => {
    setReactivating(true);
    try {
      await reactivateDossier(state.account.id, dossierId, dossier, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.reactivated") } });
      load();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setReactivating(false);
    }
  };

  const documents = transformDocuments(dossier?.documents, state.doctypeTraductions, state.lang);

  if (loading) return <LightLoader overlay label={i18next.t("light.detail.loadingDossier")} />;
  if (!dossier) return null;

  const identifiers = [
    dossier.siret && `${dossier.siret} (Siret)`,
    dossier.siren && `${dossier.siren} (Siren)`,
    dossier.tva   && `${dossier.tva} (TVA)`,
    dossier.duns  && `${dossier.duns} (DUNS)`,
  ].filter(Boolean);

  return (
    <>
    <Box className={light.pageRoot}>
      {/* Header */}
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner} style={{}}>
          <Box className={light.backBtnWhite} onClick={() => {
            const fromContextId = evalListState.fromContextId;
            if (fromContextId) {
              history.push(getUrlWithAccountId(state.account.id, `light/contexts/${fromContextId}`));
            } else if (fromEvaluations) {
              history.push({
                pathname: getUrlWithAccountId(state.account.id, ROUTES.LIGHT_EVALUATIONS),
                state: { ...evalListState, ...(evalFilters ? { filters: evalFilters } : {}), page: evalPage, ...(dossier.stoppingDate ? { archived: true } : {}) },
              });
            } else {
              history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${dossier.thirdpartyId}`));
            }
          }}>
            <ArrowBackIcon style={{ fontSize: "16px" }} />
            {evalListState.fromContextId ? i18next.t("light.common.back") : fromEvaluations ? i18next.t("light.evaluations.title") : dossier.thirdpartyName}
          </Box>
          {dossier.stoppingDate && (
            <Box style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", backgroundColor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: RADIUS.md, padding: "0.4rem 1rem", marginBottom: "0.25rem" }}>
              <Typography style={{ ...FONT.body, fontWeight: 700, color: "#FCA5A5", fontFamily: FONT.family }}>{i18next.t("light.detail.archivedEvaluation")}</Typography>
              <Typography style={{ ...FONT.small, color: "rgba(255,255,255,0.6)", fontFamily: FONT.family, marginLeft: "0.25rem" }}>
                {i18next.t("light.detail.since")} {formatDt(dossier.stoppingDate)}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", gap: "1rem", flexDirection: { xs: "column", sm: "row" } }}>
            <Box>
              <Typography className={light.pageTitleWhite}>{dossier.dossierTypeLibelle || i18next.t("light.common.freeEvaluation")}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <LightStatusChip code={dossier.statusCode} variant="filled" />
              {!dossier.stoppingDate && canArchiveDossier ? (
                <Tooltip title={i18next.t("light.detail.deactivateEvaluation")} placement="bottom" disableInteractive>
                  <button
                    onClick={() => setStopDialogOpen(true)}
                    style={{ display: "flex", alignItems: "center", gap: "0.35rem", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: RADIUS.sm, padding: "0.35rem 0.75rem", color: "rgba(255,255,255,0.85)", cursor: "pointer", fontFamily: FONT.family, ...FONT.small, fontWeight: 600, transition: "background 0.15s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
                  >
                    <StopCircleOutlinedIcon style={{ fontSize: "15px" }} />
                    {i18next.t("light.detail.deactivate")}
                  </button>
                </Tooltip>
              ) : (
                canEditDossier && (
                  <button
                    onClick={handleReactivateDossier}
                    disabled={reactivating}
                    style={{ display: "flex", alignItems: "center", gap: "0.35rem", background: BRAND.secondary, border: "none", borderRadius: RADIUS.sm, padding: "0.4rem 0.85rem", color: BRAND.primary, cursor: "pointer", fontFamily: FONT.family, ...FONT.small, fontWeight: 700, transition: "opacity 0.15s", flexShrink: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                  >
                    {reactivating ? `${i18next.t("light.detail.reactivation")}…` : i18next.t("light.detail.reactivate")}
                  </button>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className={light.pageInner} style={{ marginTop: "-0.75rem" }}>

        {/* ── Info + Indicators two-column (same layout as TP detail) ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: canViewDossierIndicators && indicators.length > 0 ? { xs: "1fr", md: "1fr 400px" } : "1fr", gap: "1rem", marginBottom: "1.25rem", alignItems: "center" }}>

        {/* Left column: TP link + attributes */}
        <Box className={light.sectionCard} style={{ marginBottom: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Box style={{ padding: "1.25rem 1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>

            {/* TP banner */}
            <Box
              component="a"
              href={getUrlWithAccountId(state.account.id, `light/thirdparties/${dossier.thirdpartyId}`)}
              onClick={(e) => { e.preventDefault(); history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${dossier.thirdpartyId}`)); }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                cursor: "pointer",
                transition: "color 0.15s",
                textDecoration: "none",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
              onMouseEnter={(e) => { e.currentTarget.querySelector('.tpLink').style.color = BRAND.primary; }}
              onMouseLeave={(e) => { e.currentTarget.querySelector('.tpLink').style.color = BRAND.textMuted; }}
            >
              <Box style={{ width: "36px", height: "36px", borderRadius: RADIUS.md, backgroundColor: `${BRAND.primary}10`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <BusinessOutlinedIcon style={{ fontSize: "17px", color: BRAND.primary }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, flexBasis: { xs: "calc(100% - 52px)", sm: "auto" } }}>
                <Typography style={{ ...FONT.h3, color: BRAND.textMain, fontFamily: FONT.family }}>{dossier.thirdpartyName}</Typography>
                {identifiers[0] && (
                  <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>{identifiers[0]}</Typography>
                )}
              </Box>
              <Typography
                className="tpLink"
                sx={{
                  ...FONT.small,
                  color: BRAND.textMuted,
                  fontFamily: FONT.family,
                  fontWeight: 600,
                  flexShrink: 0,
                  transition: "color 0.15s",
                  flexBasis: { xs: "100%", sm: "auto" },
                  textAlign: { xs: "left", sm: "left" },
                  paddingLeft: { xs: "52px", sm: 0 },
                }}
              >
                {i18next.t("light.detail.tpSheet")}
              </Typography>
            </Box>

            {/* Context */}
            {dossierContext && (
              <Box
                component="a"
                href={getUrlWithAccountId(state.account.id, `light/contexts/${dossierContext.instanceId}`)}
                onClick={(e) => { e.preventDefault(); history.push(getUrlWithAccountId(state.account.id, `light/contexts/${dossierContext.instanceId}`)); }}
                style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer", textDecoration: "none", padding: "0.6rem 0.75rem", borderRadius: RADIUS.md, backgroundColor: `${BRAND.primary}06`, border: `1px solid ${BRAND.primary}15`, transition: "background 0.12s" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${BRAND.primary}12`; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `${BRAND.primary}06`; }}
              >
                <AccountTreeOutlinedIcon style={{ fontSize: "16px", color: BRAND.primary }} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family }}>{dossierContext.modelName}</Typography>
                  <Typography style={{ ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{dossierContext.reference}</Typography>
                </Box>
              </Box>
            )}

            {/* Attributes — only if account has dossier attribute definitions */}
            {canViewDossierAttributes && hasDossierAttrDefs && <Box style={{ paddingTop: "0.6rem", borderTop: `1px solid ${BRAND.border}` }}>
              <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.4rem" }}>
                <span style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.detail.attributes")}</span>
                {canEditDossierAttributes && !dossier.stoppingDate && (
                  <button
                    onClick={() => setEditDossierAttrsOpen(true)}
                    style={{ border: "none", background: "transparent", cursor: "pointer", color: BRAND.textMuted, padding: "2px", borderRadius: RADIUS.sm, transition: "color 0.15s", display: "flex", alignItems: "center" }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                  >
                    <EditOutlinedIcon style={{ fontSize: "14px" }} />
                  </button>
                )}
              </Box>
              {dossierAttrs.length === 0 ? (
                <span style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>{i18next.t("light.detail.noAttribute")}</span>
              ) : (
                <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                  {dossierAttrs.map((a) => {
                    const vals = Array.isArray(a.values) ? a.values : [a.values];
                    return (
                      <Tooltip key={a.label} title={vals.length > 1 ? <>{vals.map((v, i) => <div key={i}>{v}</div>)}</> : `${a.label} : ${vals[0]}`} placement="top" enterDelay={200} disableInteractive>
                        <Box className={light.attrTag}>
                          <span className={light.attrTagLabel}>{a.label}</span>
                          <span className={light.attrTagValue}>{vals.join(", ")}</span>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              )}
            </Box>}
          </Box>
        </Box>

        {/* Right column: Indicators (same as TP detail) */}
        {canViewDossierIndicators && indicators.length > 0 && (
          <Box className={light.sectionCard} style={{ marginBottom: 0 }}>
            <Box className={light.sectionHead}>
              <Typography className={light.sectionTitle}>{i18next.t("light.detail.indicators")}</Typography>
              <Box className={light.sectionCount}>{indicators.length}</Box>
            </Box>
            <Box style={{ display: "flex", flexDirection: "column", gap: "0.4rem", padding: "0.75rem 1rem" }}>
              {indicators.map((ind) => {
                const color = normColor(ind.resultColorCode) || "#94A3B8";
                const val = formatCalculationResult(ind);
                const displayVal = val === true ? i18next.t("yes") : val === false ? i18next.t("no") : val;
                return (
                  <Box key={ind.name} style={{
                    backgroundColor: `${color}10`, borderRadius: RADIUS.sm,
                    border: `1px solid ${color}20`, padding: "0.5rem 0.75rem",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem",
                  }}>
                    <Typography style={{ ...FONT.small, color: BRAND.textMain, fontFamily: FONT.family, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                      {ind.label || ind.name}
                    </Typography>
                    <Typography style={{ ...FONT.small, fontWeight: 700, color, fontFamily: FONT.family, flexShrink: 0, whiteSpace: "nowrap" }}>
                      {displayVal ?? "—"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        </Box>

        {/* ── Documents ── */}
        <Box className={light.sectionCard}>
          <Box className={light.sectionHead}>
            <ArticleOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
            <Typography className={light.sectionTitle}>{i18next.t("light.detail.documents")}</Typography>
            <Box className={light.sectionCount}>{documents.length}</Box>
            {!isLocked && !isStopped && (
              <Box style={{ marginLeft: "auto" }}>
                <button
                  onClick={handleOpenAddDoc}
                  style={{ display: "flex", alignItems: "center", gap: "0.25rem", border: "none", background: "transparent", cursor: "pointer", color: BRAND.primary, ...FONT.meta, fontFamily: FONT.family, fontWeight: 600, padding: "2px 4px", borderRadius: RADIUS.sm, transition: "opacity 0.15s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >
                  <EditOutlinedIcon style={{ fontSize: "12px" }} />
                  {i18next.t("light.detail.manageDocuments")}
                </button>
              </Box>
            )}
          </Box>
          {documents.length === 0 ? (
            <Box style={{ padding: "2rem 1.5rem", textAlign: "center", color: BRAND.textMuted, fontFamily: "Inter", fontSize: "0.875rem" }}>
              {i18next.t("light.detail.noDoc")}
            </Box>
          ) : (
            <Box className={s.docList}>
              {documents.map((doc, i) => (
                <DocumentRow
                  key={doc.documentCode ?? i}
                  doc={doc}
                  showSeeAlways={false}
                  onView={handleConsultDoc}
                  onRespond={(d) => {
                    sessionStorage.setItem("light_form_return", JSON.stringify({ returnUrl: window.location.pathname, reopenDoc: { reponseId: d.reponseId, fileId: d.fileId, documentName: d.documentName, typeDocumentId: d.typeDocumentId, acquisition: d.acquisition } }));
                    window.location.href = `${getEnv("REACT_APP_API_URL_FORM")}/compteDO/${state.account.id}/document/${d.typeDocumentId}/dossier/${dossierId}`;
                  }}
                  classes={s}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* ── Notes ── */}
        <Box className={light.sectionCard}>
          <Box
            className={light.sectionHead}
            onClick={() => setNotesOpen((o) => !o)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <ChatBubbleOutlineIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
            <Typography className={light.sectionTitle}>{i18next.t("light.detail.notes")}</Typography>
            {notes.length > 0 && <Box className={light.sectionCount}>{notes.length}</Box>}
            <Box style={{ marginLeft: "auto" }}>
              <ExpandMoreIcon style={{
                fontSize: "18px", color: BRAND.textMuted,
                transform: notesOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }} />
            </Box>
          </Box>

          {notesOpen && (
            <>
              {/* Input */}
              {canCreateDossierNotes && !dossier.stoppingDate && <Box className={s.noteInput}>
                <Box className={s.noteInputWrap} style={{ flex: 1 }}>
                  {mention.mentionQuery !== null && mention.filteredUsers.length > 0 && (
                    <Box className={s.mentionDropdown} ref={mention.dropdownRef}>
                      {mention.filteredUsers.map((u, idx) => {
                        const full = `${u.firstname ?? ""} ${u.name ?? ""}`.trim();
                        const initials = [u.firstname?.[0], u.name?.[0]].filter(Boolean).join("").toUpperCase();
                        return (
                          <Box
                            key={u.userId ?? idx}
                            className={`${s.mentionItem} ${idx === mention.mentionIndex ? s.mentionItemActive : ""}`}
                            onMouseDown={(e) => { e.preventDefault(); mention.insertMention(u); }}
                          >
                            <Box className={s.mentionAvatar}>{initials}</Box>
                            <Typography className={s.mentionName}>{full}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  <textarea
                    ref={mention.textareaRef}
                    className={s.noteTextarea}
                    style={{ width: "100%" }}
                    placeholder={i18next.t("light.detail.addNote")}
                    value={noteText}
                    onChange={mention.handleTextChange}
                    onKeyDown={(e) => mention.handleKeyDown(e, handleAddNote)}
                    rows={2}
                  />
                </Box>
                <button className={s.noteSendBtn} onClick={handleAddNote} disabled={!noteText.trim() || savingNote}>
                  <SendIcon style={{ fontSize: "16px" }} />
                </button>
              </Box>}

              {/* List */}
              {canViewDossierNotes && (notes.length === 0 ? (
                <Box className={s.emptyNote}>{i18next.t("light.detail.noNote")}</Box>
              ) : (
                <Box className={s.notesList}>
                  <LightNotesList notes={notes} onDelete={handleDeleteNote} canDelete={!dossier.stoppingDate && canCreateDossierNotes} classes={s} />
                </Box>
              ))}
            </>
          )}
        </Box>

      </Box>
    </Box>

    {/* ── Dialog attributs dossier ── */}
    <Dialog
      open={editDossierAttrsOpen}
      onClose={() => setEditDossierAttrsOpen(false)}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, maxHeight: "85vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5, flexShrink: 0 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <FolderOutlinedIcon style={{ fontSize: "18px", color: BRAND.secondary }} />
          {i18next.t("light.detail.attributes")} — {dossier?.dossierTypeLibelle}
        </Box>
        <IconButton size="small" onClick={() => setEditDossierAttrsOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 3, overflowY: "auto" }}>
        <LightEditableAttributes
          accountId={state.account?.id}
          entityId={dossierId}
          bondType="dossier"
          onSaved={() => {
            getDossierAttributes(state.account.id, dossierId, null)
              .then((r) => setDossierAttrs((r?.content ?? []).filter((a) => a.values?.length > 0)))
              .catch(() => {});
          }}
        />
      </DialogContent>
    </Dialog>

    {/* Manage documents dialog */}
    <Dialog
      open={addDocOpen}
      onClose={() => setAddDocOpen(false)}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, maxHeight: "85vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5, flexShrink: 0 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ArticleOutlinedIcon style={{ fontSize: "18px", color: BRAND.secondary }} />
          {i18next.t("light.detail.manageDocuments")}
        </Box>
        <IconButton size="small" onClick={() => setAddDocOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1, overflowY: "auto" }}>
        {accountReqs === null ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <LightLoader size="sm" label={i18next.t("light.detail.loadingDocuments")} />
          </Box>
        ) : (() => {
          const allDocs = accountReqs.filter((r) => !isSealedDocument(r.code));
          const filtered = docFilter.trim()
            ? allDocs.filter((r) => (r.descriptionFr || r.name || r.code || "").toLowerCase().includes(docFilter.trim().toLowerCase()))
            : allDocs;
          const sorted = [...filtered].sort((a, b) => {
            const aS = selectedNewDocs.includes(a.code) ? 0 : 1;
            const bS = selectedNewDocs.includes(b.code) ? 0 : 1;
            if (aS !== bS) return aS - bS;
            return (a.descriptionFr || a.name || a.code).localeCompare(b.descriptionFr || b.name || b.code);
          });
          return (
            <Box style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <input
                className={light.fieldInput}
                placeholder={i18next.t("light.detail.searchDoc")}
                value={docFilter}
                onChange={(e) => setDocFilter(e.target.value)}
                autoFocus
                style={{ marginBottom: "0.25rem" }}
              />
              <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>
                {i18next.t("light.detail.selectedDocs", { count: selectedNewDocs.length })}
              </Typography>
              {filtered.length === 0 ? (
                <Box style={{ textAlign: "center", padding: "1rem", ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>
                  {i18next.t("light.detail.noDocMatch")}
                </Box>
              ) : sorted.map((req) => {
                const isSelected = selectedNewDocs.includes(req.code);
                return (
                  <Box
                    key={req.code}
                    onClick={() => setSelectedNewDocs((prev) => prev.includes(req.code) ? prev.filter((c) => c !== req.code) : [...prev, req.code])}
                    style={{
                      display: "flex", alignItems: "center", gap: "0.75rem",
                      padding: "0.65rem 1rem", borderRadius: RADIUS.md, cursor: "pointer",
                      border: `1.5px solid ${isSelected ? BRAND.primary : BRAND.border}`,
                      backgroundColor: isSelected ? `${BRAND.primary}06` : BRAND.white,
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                  >
                    <Box style={{
                      width: "20px", height: "20px", borderRadius: "4px", flexShrink: 0,
                      border: `2px solid ${isSelected ? BRAND.primary : BRAND.border}`,
                      backgroundColor: isSelected ? BRAND.primary : BRAND.white,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.15s",
                    }}>
                      {isSelected && <span style={{ color: BRAND.white, fontSize: "12px", fontWeight: 700, lineHeight: 1 }}>✓</span>}
                    </Box>
                    <Typography style={{ ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{getDoctypeTranslatedName(state.doctypeTraductions, req.code, state.lang) || req.descriptionFr || req.name || req.code}</Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })()}
      </DialogContent>
      <Box style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", padding: "0.75rem 1.5rem", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
        <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={() => setAddDocOpen(false)}>
          {i18next.t("cancel")}
        </button>
        <button className={light.btnPrimary} style={{ padding: "0.5rem 1.25rem" }} onClick={handleConfirmDocs} disabled={!(() => { const s = [...selectedNewDocs].sort(); const c = [...documents.map((d) => d.documentCode)].sort(); return s.length !== c.length || s.some((v, i) => v !== c[i]); })() || addingDocs}>
          {addingDocs ? `${i18next.t("save")}…` : i18next.t("save")}
        </button>
      </Box>
    </Dialog>

    <StopConfirmDialog
      open={stopDialogOpen}
      onClose={() => { setStopDialogOpen(false); setStopReason(""); }}
      onConfirm={handleStopDossier}
      loading={stopping}
      title={i18next.t("light.detail.deactivateEvaluation")}
      reasonLabel={i18next.t("light.detail.stopReason")}
      reasonPlaceholder={i18next.t("light.detail.stopReasonPlaceholder")}
      reason={stopReason}
      onReasonChange={setStopReason}
      cancelLabel={i18next.t("cancel")}
      confirmLabel={i18next.t("light.detail.deactivate")}
      confirmingLabel={`${i18next.t("light.detail.deactivation")}…`}
    >
      {i18next.t("light.detail.stopConfirm", { name: dossier?.dossierTypeLibelle || i18next.t("light.common.freeEvaluation") })} {i18next.t("light.detail.stopReversible")}
    </StopConfirmDialog>

    <LightDocViewer
      open={Boolean(viewerDoc)}
      onClose={() => { setViewerDoc(null); load(true); }}
      reponseId={viewerDoc?.reponseId}
      fileId={viewerDoc?.fileId}
      dossierId={dossierId}
      thirdpartyId={dossier?.thirdpartyId}
      docName={viewerDoc?.documentName}
      typeDocumentId={viewerDoc?.typeDocumentId}
      acquisition={viewerDoc?.acquisition}
      evidences={viewerDoc?.evidences}
      onStatusChanged={load}
    />
    </>
  );
}
